import { useEffect, useMemo, useState, useContext } from "react";
import { Modal } from "react-bootstrap";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { TextAreaField } from "@stadium/ui/src/components/input/bs-input";

import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";

import UpdateExistingCartItemPreferencesBtn from "@components/store/product-detail/shared/update-cart-item-collection-product";

import {
  getDefaultAskForPreferenceMessage,
  getInternationalDietaryRestrictions,
} from "@selectors/config";
import { useConfig } from "@stadium/hooks/lib/use-config";

import AddToBagBtn from "@components/store/product-detail/shared/add-to-bag";

import modalDtry from "./main.module.scss";

import { DIETARY_RESTRICTION_OTHER_KEY } from "@constants/products/detail";

const emptyStr = "";
const emptyObj = {};

const useInternationalRestrictionsModal = ({
  previouslySelectedRestriction = emptyStr,
  previouslySelectedPreference = emptyStr,
  askForRestriction,
  askForPreference,
  askForPreferenceMessage = "",
  isSwagProduct = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRestriction, setSelectedRestriction] = useState(emptyStr);
  const [otherRestrictions, setOtherRestrictions] = useState(emptyStr);
  const [preferences, setPreferences] = useState(emptyStr);
  const restrictions = useConfig(getInternationalDietaryRestrictions);
  const defaulAskForPreferenceMessage = useConfig(getDefaultAskForPreferenceMessage);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  useEffect(() => {
    if (isOpen) {
      setSelectedRestriction(previouslySelectedRestriction);
      setPreferences(previouslySelectedPreference);
    } else {
      setSelectedRestriction(emptyStr);
      setPreferences(emptyStr);
    }
  }, [previouslySelectedRestriction, previouslySelectedPreference, isOpen]);

  const toggleRestrictionsModal = () => setIsOpen((prevState) => !prevState);

  const otherSelected = selectedRestriction === DIETARY_RESTRICTION_OTHER_KEY;
  const isValuePresent =
    (askForPreference && (!isSwagProduct || preferences.trim())) ||
    (askForRestriction && (selectedRestriction || otherSelected));


  const renderLabelContent = (optionData) => {
    return (
      <div className={modalDtry.rdBtnFlex}>
        <span className={modalDtry.radioBtnCircle}></span>
        <span className={modalDtry.rdOptionText}>{optionData.text}</span>
      </div>
    );
  };

  const renderRestrictionsOption = (optionData) => (
    <RadioInput
      value={optionData.key}
      id={optionData.key}
      checked={selectedRestriction === optionData.key}
      name="dietaryRestriction"
      key={`${optionData.text}-${optionData.key}`}
      onClick={() => setSelectedRestriction(optionData.key)}
      label={
        <Label iconType="radioOutline" className={modalDtry.inputLabel}>
          {renderLabelContent(optionData)}{" "}
        </Label>
      }
      classes={modalDtry.radioButton}
    />
  );
  const renderOtherRestrictionsTextbox = () => {
    if (!otherSelected) return null;
    return (
      <div>
        <TextAreaField
          id="otherRestrictions"
          value={otherRestrictions}
          placeholder="Other Restrictions"
          onChange={setOtherRestrictions}
          className={modalDtry.textAreaFormControl}
        />
      </div>
    );
  };

  const renderPreferencesTextbox = () => {
    return (
      <div>
        <TextAreaField
          id="preferences"
          value={preferences}
          placeholder="Type your answer here"
          onChange={setPreferences}
          className={modalDtry.formField}
        />
      </div>
    );
  };

  const renderDietaryRestrictionsSection = () => (
    <>
      <div className={modalDtry.titleHeading}>Select Dietary Restriction(s)</div>
      <p className={modalDtry.subHeading}>Any dietary restriction we should know?</p>
      {restrictions.map(renderRestrictionsOption)}
      {renderOtherRestrictionsTextbox()}
    </>
  );

  const renderPreferencesSection = () => (
    <>
      <div className={modalDtry.titleHeading}>Preferences</div>
      <p className={modalDtry.subHeading}>
        {askForPreferenceMessage || defaulAskForPreferenceMessage}
      </p>
      {renderPreferencesTextbox()}
    </>
  );

  const renderModalBody = () => {
    if (askForPreference) return renderPreferencesSection();
    return renderDietaryRestrictionsSection();
  };

  const successHandler = ({ onSuccessCallback } = {}) => {
    onSuccessCallback?.();
    toggleRestrictionsModal();
  };

  const selectedRestrictionsList = useMemo(() => {
    if (!selectedRestriction) return [];
    if (otherSelected) {
      return [{ id: DIETARY_RESTRICTION_OTHER_KEY, name: `Other: ${otherRestrictions}` }];
    }
    const matched = restrictions.find((r) => r.key === selectedRestriction);
    return matched ? [{ id: matched.key, name: matched.text }] : [];
  }, [selectedRestriction, otherSelected, otherRestrictions, restrictions]);

  const renderAddToBagBtn = ({ product, quantity, variant }) => (
    <AddToBagBtn
      product={product}
      quantity={quantity}
      variant={variant}
      restrictions={selectedRestrictionsList}
      preferences={preferences}
      onSuccessCallback={successHandler}
      isDisabled={!isValuePresent}
    />
  );
  const renderUpdateExistingItemPreferencesBtn = ({ lineItem, onSuccessCallback }) => (
    <UpdateExistingCartItemPreferencesBtn
      lineItem={lineItem}
      restrictions={selectedRestrictionsList}
      preferences={preferences}
      onSuccessCallback={() => successHandler({ onSuccessCallback })}
    />
  );

  const renderRestrictionsModal = ({
    product = emptyObj,
    quantity = null,
    variant = emptyObj,
    lineItem = emptyObj,
    isUpdateExistingItemInCart = false,
    onSuccessCallback = () => {},
    renderAddToBagCta = null,
  }) => {
    return (
      <Modal
        data-acsb-overlay="popup"
        show={isOpen}
        animation={false}
        dialogClassName={`${modalDtry.modalDialog}`}
        className={`${modalDtry.dietaryModal} ${modalDtry.dietaryRestrictModal} `}
        onHide={toggleRestrictionsModal}
      >
        <Modal.Header className={modalDtry.modalHeader}>
          <i className={modalDtry.iconClose} onClick={toggleRestrictionsModal} />
        </Modal.Header>
        <Modal.Body className={modalDtry.modalBody}>
          <BottomIconHideWrapper hideIcon={isMobileDevice}>
            {renderModalBody()}
          </BottomIconHideWrapper>
        </Modal.Body>
        <Modal.Footer className={modalDtry.modalFooter}>
          {renderAddToBagCta ? (
            renderAddToBagCta(preferences)
          ) : (
            <>
              {isUpdateExistingItemInCart
                ? renderUpdateExistingItemPreferencesBtn({ lineItem, onSuccessCallback })
                : renderAddToBagBtn({ product, quantity, variant })}
            </>
          )}
        </Modal.Footer>
      </Modal>
    );
  };

  return { selectedRestriction, toggleRestrictionsModal, renderRestrictionsModal };
};

export default useInternationalRestrictionsModal;
